import { motion } from "framer-motion";
import { ArrowLeft, FileText, Shield, UserCheck, AlertTriangle, Scale, Mail } from "lucide-react";
import { Link } from "react-router-dom";

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

// Nephrix Logo Component
const NephrixIcon = ({ className = "h-8 w-8" }) => (
  <img 
    src="/nephrix-logo.png" 
    alt="Nephrix" 
    className={`${className} object-contain`}
  />
);

const TermsOfService = () => {
  const currentDate = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-lg border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <NephrixIcon className="h-10 w-10" />
          </Link>
          <Link 
            to="/" 
            className="flex items-center gap-2 text-slate-600 hover:text-blue-600 transition-colors text-sm font-medium"
            data-testid="back-home"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-16">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeInUp}
        >
          {/* Title */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-blue-100 mb-6">
              <FileText className="w-8 h-8 text-blue-600" />
            </div>
            <h1 
              className="text-4xl md:text-5xl font-bold text-slate-900 tracking-tight mb-4"
              style={{ fontFamily: 'Manrope, sans-serif' }}
              data-testid="terms-title"
            >
              Terms of Service
            </h1>
            <p className="text-slate-500 text-lg">Last updated: {currentDate}</p>
          </div>

          {/* Terms Content */}
          <div className="space-y-12">
            
            {/* Acceptance of Terms */}
            <section className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-blue-600" />
                </div>
                <h2 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  1. Acceptance of Terms
                </h2>
              </div>
              <p className="text-slate-600 leading-relaxed">
                Welcome to Nephrix. By accessing or using our mobile application and website (collectively, the "Service"), 
                you agree to be bound by these Terms of Service ("Terms"). Please read them carefully before using Nephrix.
              </p>
              <p className="text-slate-600 leading-relaxed mt-4">
                If you do not agree to these Terms, you may not access or use the Service. 
                By creating an account, downloading the app, or using any part of the Service, you acknowledge that you have read, 
                understood, and agree to be bound by these Terms and our Privacy Policy.
              </p>
            </section>

            {/* Description of Service */}
            <section className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                2. Description of Service
              </h2>
              <p className="text-slate-600 leading-relaxed">
                Nephrix is an AI-powered health management and wellness tracking application designed specifically for dialysis and chronic kidney disease patients. 
                The Service provides tools to help users:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2 mt-4">
                <li>Track and manage daily fluid intake</li>
                <li>Monitor weight and health metrics</li>
                <li>Log dialysis sessions and treatments</li>
                <li>Access AI-powered insights and educational information</li>
                <li>Track nutrition and dietary patterns</li>
                <li>Manage wellness routines and reminders</li>
              </ul>
            </section>

            {/* No Medical Advice */}
            <section className="bg-amber-50 rounded-2xl p-8 border border-amber-100">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h2 className="text-2xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    3. No Medical Advice
                  </h2>
                  <p className="text-slate-700 leading-relaxed font-medium">
                    IMPORTANT: Please read this section carefully.
                  </p>
                  <p className="text-slate-700 leading-relaxed mt-3">
                    Nephrix is designed as a health management and educational tool only. The Service:
                  </p>
                  <ul className="list-disc pl-6 text-slate-700 space-y-2 mt-3">
                    <li><strong>Does NOT provide medical advice, diagnosis, or treatment</strong></li>
                    <li><strong>Does NOT replace professional medical care</strong></li>
                    <li><strong>Does NOT create a physician-patient relationship</strong></li>
                    <li><strong>Should NOT be used for emergency medical situations</strong></li>
                  </ul>
                  <p className="text-slate-700 leading-relaxed mt-4">
                    AI-generated content and insights are for informational purposes only and should not be relied upon for medical decisions. 
                    Always consult your nephrologist, dialysis care team, or other qualified healthcare professionals before making any health-related decisions.
                  </p>
                  <p className="text-slate-700 leading-relaxed mt-3 font-medium">
                    In case of a medical emergency, contact emergency services (911 or your local emergency number) immediately.
                  </p>
                </div>
              </div>
            </section>

            {/* Eligibility */}
            <section className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center">
                  <UserCheck className="w-5 h-5 text-green-600" />
                </div>
                <h2 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  4. Eligibility
                </h2>
              </div>
              <p className="text-slate-600 leading-relaxed">
                To use Nephrix, you must:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2 mt-4">
                <li>Be at least 18 years of age</li>
                <li>Have the legal capacity to enter into a binding agreement</li>
                <li>Not be prohibited from using the Service under applicable laws</li>
                <li>Provide accurate and complete registration information</li>
              </ul>
              <p className="text-slate-600 leading-relaxed mt-4">
                If you are using the Service on behalf of another person (such as a family member or patient under your care), 
                you represent that you have the authority to bind that person to these Terms.
              </p>
            </section>

            {/* User Accounts */}
            <section className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                5. User Accounts
              </h2>
              <p className="text-slate-600 leading-relaxed">
                When you create an account with Nephrix, you agree to:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2 mt-4">
                <li>Provide accurate, current, and complete information</li>
                <li>Maintain and promptly update your account information</li>
                <li>Keep your password secure and confidential</li>
                <li>Notify us immediately of any unauthorized access to your account</li>
                <li>Accept responsibility for all activities that occur under your account</li>
              </ul>
              <p className="text-slate-600 leading-relaxed mt-4">
                We reserve the right to suspend or terminate accounts that violate these Terms or engage in suspicious activity.
              </p>
            </section>

            {/* Acceptable Use */}
            <section className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                6. Acceptable Use
              </h2>
              <p className="text-slate-600 leading-relaxed">
                When using Nephrix, you agree NOT to:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2 mt-4">
                <li>Use the Service for any unlawful purpose or in violation of any laws</li>
                <li>Provide false, misleading, or fraudulent information</li>
                <li>Attempt to access other users' accounts or personal data</li>
                <li>Interfere with or disrupt the Service or servers</li>
                <li>Reverse engineer, decompile, or attempt to extract source code</li>
                <li>Use automated systems, bots, or scripts without permission</li>
                <li>Transmit malware, viruses, or other harmful code</li>
                <li>Harass, abuse, or harm other users</li>
                <li>Use the Service to provide medical advice to others</li>
              </ul>
            </section>

            {/* AI Features */}
            <section className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                7. AI Features
              </h2>
              <p className="text-slate-600 leading-relaxed">
                Nephrix incorporates artificial intelligence features to provide personalized insights and information. 
                By using these features, you acknowledge and agree that:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2 mt-4">
                <li>AI responses are generated by automated systems and may not always be accurate</li>
                <li>AI features are for informational and educational purposes only</li>
                <li>AI cannot replace professional medical judgment or advice</li>
                <li>You should verify important information with qualified healthcare providers</li>
                <li>We may use your interactions to improve AI accuracy (in accordance with our Privacy Policy)</li>
              </ul>
            </section>

            {/* Intellectual Property */}
            <section className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                8. Intellectual Property
              </h2>
              <p className="text-slate-600 leading-relaxed">
                The Nephrix name, logo, and all related content, features, and functionality are owned by Nephrix and are protected by 
                international copyright, trademark, patent, trade secret, and other intellectual property laws.
              </p>
              <p className="text-slate-600 leading-relaxed mt-4">
                You may not copy, modify, distribute, sell, or lease any part of our Service or included content, 
                nor may you reverse engineer or attempt to extract the source code of the software, unless permitted by law.
              </p>
            </section>

            {/* Limitation of Liability */}
            <section className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-purple-600" />
                </div>
                <h2 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  9. Limitation of Liability
                </h2>
              </div>
              <p className="text-slate-600 leading-relaxed">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, NEPHRIX AND ITS AFFILIATES, OFFICERS, DIRECTORS, EMPLOYEES, AND AGENTS 
                SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2 mt-4">
                <li>Loss of profits, data, or goodwill</li>
                <li>Health outcomes or medical complications</li>
                <li>Service interruption or errors</li>
                <li>Unauthorized access to your account or data</li>
                <li>Any damages arising from your use of or inability to use the Service</li>
              </ul>
              <p className="text-slate-600 leading-relaxed mt-4">
                The Service is provided "as is" without warranties of any kind, either express or implied.
              </p>
            </section>

            {/* Service Availability */}
            <section className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                10. Service Availability
              </h2>
              <p className="text-slate-600 leading-relaxed">
                We strive to provide reliable and continuous access to Nephrix. However, we do not guarantee uninterrupted availability. 
                The Service may be temporarily unavailable due to:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2 mt-4">
                <li>Scheduled maintenance and updates</li>
                <li>Technical issues or server problems</li>
                <li>Circumstances beyond our control</li>
              </ul>
              <p className="text-slate-600 leading-relaxed mt-4">
                We reserve the right to modify, suspend, or discontinue any part of the Service at any time without prior notice.
              </p>
            </section>

            {/* Termination */}
            <section className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                11. Termination
              </h2>
              <p className="text-slate-600 leading-relaxed">
                We may terminate or suspend your access to the Service immediately, without prior notice or liability, for any reason, 
                including if you breach these Terms. Upon termination:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2 mt-4">
                <li>Your right to use the Service will immediately cease</li>
                <li>You may request deletion of your account and data</li>
                <li>Provisions that by their nature should survive termination will remain in effect</li>
              </ul>
              <p className="text-slate-600 leading-relaxed mt-4">
                You may terminate your account at any time by contacting us or using the account deletion feature in the app.
              </p>
            </section>

            {/* Governing Law */}
            <section className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-teal-50 flex items-center justify-center">
                  <Scale className="w-5 h-5 text-teal-600" />
                </div>
                <h2 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  12. Governing Law
                </h2>
              </div>
              <p className="text-slate-600 leading-relaxed">
                These Terms shall be governed by and construed in accordance with applicable laws, without regard to conflict of law principles. 
                Any disputes arising from these Terms or the Service shall be resolved through binding arbitration or in the courts of competent jurisdiction.
              </p>
            </section>

            {/* Changes to Terms */}
            <section className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                13. Changes to Terms
              </h2>
              <p className="text-slate-600 leading-relaxed">
                We reserve the right to modify these Terms at any time. When we make changes, we will update the "Last updated" date at the top of this page 
                and may provide additional notice through the app or via email.
              </p>
              <p className="text-slate-600 leading-relaxed mt-4">
                Your continued use of the Service after any changes constitutes acceptance of the modified Terms. 
                If you do not agree to the new Terms, you must stop using the Service.
              </p>
            </section>

            {/* Contact Information */}
            <section className="bg-gradient-to-br from-blue-50 to-slate-50 rounded-2xl p-8 border border-blue-100">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-blue-600" />
                </div>
                <h2 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  14. Contact Information
                </h2>
              </div>
              <p className="text-slate-600 leading-relaxed mb-6">
                If you have any questions or concerns about these Terms of Service, please contact us:
              </p>
              <div className="bg-white rounded-xl p-6 border border-slate-100">
                <p className="text-slate-700">
                  <strong>Support:</strong>{" "}
                  <a href="mailto:support@nephrixapp.com" className="text-blue-600 hover:underline">
                    support@nephrixapp.com
                  </a>
                </p>
                <p className="text-slate-700 mt-2">
                  <strong>Website:</strong>{" "}
                  <a href="https://www.nephrixapp.com" className="text-blue-600 hover:underline">
                    www.nephrixapp.com
                  </a>
                </p>
              </div>
            </section>
          </div>

          {/* Back to Home */}
          <div className="mt-16 text-center">
            <Link 
              to="/"
              className="inline-flex items-center gap-2 px-8 py-4 bg-blue-600 text-white rounded-full font-medium hover:bg-blue-700 transition-all hover:-translate-y-0.5 shadow-lg shadow-blue-500/25"
              data-testid="back-home-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-100 py-8 mt-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center">
              <NephrixIcon className="h-10 w-10" />
            </div>
            <div className="flex items-center gap-6 text-sm text-slate-500">
              <Link to="/privacy" className="hover:text-blue-600 transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-blue-600 transition-colors">Terms of Service</Link>
              <a href="mailto:support@nephrixapp.com" className="hover:text-blue-600 transition-colors">support@nephrixapp.com</a>
            </div>
          </div>
          <div className="mt-6 pt-6 border-t border-slate-100 text-center">
            <p className="text-xs text-slate-400">
              Nephrix does not provide medical diagnosis and does not replace professional medical advice.
            </p>
            <p className="text-xs text-slate-400 mt-2">
              © {new Date().getFullYear()} Nephrix. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfService;
